import create from "zustand";
import { format, startOfMonth, endOfMonth } from "date-fns";
import {
  fetchResidualPortfolio,
  fetchResidualsData,
  fetchResidualScorecard,
  fetchResidualFilterData,
  fetchPaymentAuthorizationReport,
  fetchAgentAccountStatementReport,
} from "../services/residuals";
import { subMonths } from "date-fns";

const prevMonth = subMonths(new Date(), 1);
const currentMonthStartDate = format(startOfMonth(prevMonth), "MM/dd/yyyy");
const currentMonthEndDate = format(endOfMonth(prevMonth), "MM/dd/yyyy");

const defaultPaymentAuthAppliedPeriod = () => {
  const d = subMonths(new Date(), 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const defaultAgentStatementAppliedPeriod = () => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const useResidualStore = create((set, get) => ({
  // Filters
  filters: {
    searchTerm: "",
    dateRange: {
      startDate: currentMonthStartDate,
      endDate: currentMonthEndDate,
      rangeType: "MTD",
    },
    cards: [],
    offices: [],
    branches: [],
    repCodes: [],
    orgFilter: "",
    selectedFilters: [],
    sort: {
      field: "dba",
      direction: "asc",
    },
    pagination: {
      page: 1,
      per_page: 10,
    },
    displayMode: "hierarchy",
  },

  // Data
  residuals: {
    processors: [], // Will store flattened office data
    products: {}, // Will store aggregated product data
    raw: [], // Will store the raw API response
    pagination: {
      total: 0,
      per_page: 10,
      current_page: 1,
      last_page: 1,
    },
    // Mode the currently loaded data was fetched for. Compared against
    // filters.displayMode to detect a pending mode switch — when they differ
    // we show the skeleton so the wrong-shaped rows don't render in the new
    // table layout (e.g. flat rows showing in the hierarchy table without
    // expand/collapse arrows).
    dataMode: "hierarchy",
  },
  portfolio: {
    total_volume: "0.00",
    total_last_month_residual: "0.00",
    total_projected_residual: "0.00",
  },
  scorecard: {},
  loading: {
    portfolio: false,
    residuals: false,
    scorecard: false,
  },
  error: {
    portfolio: null,
    residuals: null,
    scorecard: null,
  },
  selectedReport: "",
  setSelectedReport: (r) => set({ selectedReport: r }),

  paymentAuthOfficeOptions: [],
  paymentAuthSelectedOffice: "",
  paymentAuthOfficesLoading: false,
  paymentAuthOfficesInitialized: false,
  paymentAuthAppliedPeriod: defaultPaymentAuthAppliedPeriod(),
  setPaymentAuthSelectedOffice: (office) =>
    set({ paymentAuthSelectedOffice: office, agentStatementPage: 1 }),

  setPaymentAuthAppliedPeriod: (period) =>
    set({ paymentAuthAppliedPeriod: period }),
  paymentAuthReportData: null,
  paymentAuthReportLoading: false,

  agentStatementAppliedPeriod: defaultAgentStatementAppliedPeriod(),
  setAgentStatementAppliedPeriod: (period) =>
    set({
      agentStatementAppliedPeriod: period,
      agentStatementPage: 1,
    }),
  agentStatementReportData: null,
  agentStatementReportLoading: false,
  agentStatementPage: 1,
  agentStatementPerPage: 25,
  setAgentStatementPage: (page) => set({ agentStatementPage: page }),

  // Actions
  filterApplied: false,
  setFilterApplied: (value) => set({ filterApplied: value }),
  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),

  setLoading: (key, value) =>
    set((state) => ({
      loading: { ...state.loading, [key]: value },
    })),

  setError: (key, value) =>
    set((state) => ({
      error: { ...state.error, [key]: value },
    })),

  // Data setters
  setResiduals: (response, dataMode) =>
    set((state) => ({
      residuals: {
        ...state.residuals,
        processors: response.data || [], // Use the data array directly as processors
        raw: response.data || [], // Store raw data for reference
        products: {}, // Keep products empty for now until we implement that view
        pagination: {
          total: response.pagination?.total || 0,
          per_page: response.pagination?.per_page || 10,
          current_page: response.pagination?.current_page || 1,
          last_page: response.pagination?.last_page || 1,
        },
        dataMode: dataMode || state.residuals.dataMode,
      },
    })),
  setPortfolio: (portfolio) => set({ portfolio }),
  setScorecard: (scorecard) => set({ scorecard }),

  // Fetch actions
  fetchPortfolioData: async () => {
    const store = get();
    try {
      store.setLoading("portfolio", true);
      store.setError("portfolio", null);
      const data = await fetchResidualPortfolio(get().filters);
      store.setPortfolio({
        total_volume: data.total_volume ?? "0.00",
        total_last_month_residual: data.total_last_month_residual ?? "0.00",
        total_projected_residual: data.total_projected_residual ?? null,
      });
    } catch (error) {
      store.setError("portfolio", error.message);
    } finally {
      store.setLoading("portfolio", false);
    }
  },

  fetchResidualsExportData: async (filters, isExport) => {
    const store = get();
    try {
      const data = await fetchResidualsData(filters, isExport);
      return data;
    } catch (error) {
      store.setError("residuals", error.message);
    } finally {
      store.setLoading("residuals", false);
    }
  },

  fetchResidualsData: async (filters, isExport) => {
    const store = get();
    // Version counter on the store so an earlier in-flight fetch can't clobber
    // a newer view's data. Without this, rapidly toggling display_mode lets the
    // slower response win and leaves the table rendering wrong-shaped data.
    const reqId = (store._residualsFetchId || 0) + 1;
    set({ _residualsFetchId: reqId });
    const requestedMode = filters.displayMode || "hierarchy";
    try {
      store.setLoading("residuals", true);
      store.setError("residuals", null);
      const data = await fetchResidualsData(filters);
      if (get()._residualsFetchId !== reqId) return;
      store.setResiduals(data, requestedMode);
    } catch (error) {
      if (get()._residualsFetchId !== reqId) return;
      store.setError("residuals", error.message);
      // Resync dataMode to the requested mode so the loading-skeleton (which
      // gates on filterMode !== dataMode) clears. Without this the table
      // stays stuck in skeleton forever after a failed mode-toggle fetch.
      store.setResiduals({ data: [], pagination: {} }, requestedMode);
    } finally {
      if (get()._residualsFetchId === reqId) {
        store.setLoading("residuals", false);
      }
    }
  },

  fetchScorecardData: async () => {
    const store = get();
    try {
      store.setLoading("scorecard", true);
      store.setError("scorecard", null);
      const data = await fetchResidualScorecard();
      store.setScorecard(data);
    } catch (error) {
      store.setError("scorecard", error.message);
    } finally {
      store.setLoading("scorecard", false);
    }
  },

  // Reset state
  reset: () =>
    set({
      filters: {
        searchTerm: "",
        dateRange: {
          startDate: format(startOfMonth(new Date()), "MM/dd/yyyy"),
          endDate: format(new Date(), "MM/dd/yyyy"),
          rangeType: "MTD",
        },
        cards: [],
        offices: [],
        branches: [],
        repCodes: [],
        orgFilter: "",
        selectedFilters: [],
        displayMode: "hierarchy",
      },
      residuals: {
        processors: [], // Array of office data from API
        products: {}, // Will be implemented later
        dataMode: "hierarchy",
      },
      portfolio: {
        total_volume: "0.00",
        total_last_month_residual: "0.00",
        total_projected_residual: "0.00",
      },

      scorecard: {},
      loading: {
        portfolio: false,

        residuals: false,
        scorecard: false,
      },
      error: {
        portfolio: null,

        residuals: null,
        scorecard: null,
      },
      paymentAuthOfficeOptions: [],
      paymentAuthSelectedOffice: "",
      paymentAuthOfficesLoading: false,
      paymentAuthOfficesInitialized: false,
      paymentAuthAppliedPeriod: defaultPaymentAuthAppliedPeriod(),
      paymentAuthReportData: null,
      paymentAuthReportLoading: false,
      agentStatementAppliedPeriod: defaultAgentStatementAppliedPeriod(),
      agentStatementReportData: null,
      agentStatementReportLoading: false,
      agentStatementPage: 1,
      agentStatementPerPage: 25,
    }),
}));

export async function loadPaymentAuthOffices() {
  const state = useResidualStore.getState();
  if (state.paymentAuthOfficesInitialized || state.paymentAuthOfficesLoading) {
    return;
  }
  useResidualStore.setState({ paymentAuthOfficesLoading: true });
  try {
    const data = await fetchResidualFilterData();
    const raw = data?.office ?? [];
    const opts = [...raw]
      .sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true }),
      )
      .map((o) => ({ value: String(o), label: String(o) }));
    useResidualStore.setState((prev) => ({
      paymentAuthOfficeOptions: opts,
      paymentAuthSelectedOffice:
        prev.paymentAuthSelectedOffice || opts[0]?.value || "",
      paymentAuthOfficesInitialized: true,
    }));
  } catch {
    useResidualStore.setState({
      paymentAuthOfficeOptions: [],
      paymentAuthOfficesInitialized: false,
    });
  } finally {
    useResidualStore.setState({ paymentAuthOfficesLoading: false });
  }
}

export async function loadAgentAccountStatementReport() {
  const {
    paymentAuthSelectedOffice: office,
    agentStatementAppliedPeriod: period,
    paymentAuthOfficesLoading: officesLoading,
    agentStatementPage: page,
    agentStatementPerPage: perPage,
  } = useResidualStore.getState();
  if (!office || officesLoading) {
    return;
  }
  useResidualStore.setState({ agentStatementReportLoading: true });
  try {
    const body = await fetchAgentAccountStatementReport({
      office,
      year: period.year,
      month: period.month,
      page,
      per_page: perPage,
    });
    const data = body?.data;
    const reportData =
      data && !Array.isArray(data) && typeof data === "object" ? data : null;
    useResidualStore.setState({
      agentStatementReportData: reportData,
    });
  } catch (error) {
    console.error(error);
    useResidualStore.setState({
      agentStatementReportData: null,
    });
  } finally {
    useResidualStore.setState({ agentStatementReportLoading: false });
  }
}

export async function loadPaymentAuthorizationReport() {
  const {
    paymentAuthSelectedOffice: office,
    paymentAuthAppliedPeriod: period,
    paymentAuthOfficesLoading: officesLoading,
  } = useResidualStore.getState();
  if (!office || officesLoading) {
    return;
  }
  useResidualStore.setState({ paymentAuthReportLoading: true });
  try {
    const body = await fetchPaymentAuthorizationReport({
      office,
      year: period.year,
      month: period.month,
    });
    const data = body?.data;
    const reportData =
      data && !Array.isArray(data) && typeof data === "object" ? data : null;
    useResidualStore.setState({
      paymentAuthReportData: reportData,
    });
  } catch (error) {
    console.error(error);
    // toast.error(error.message);
    useResidualStore.setState({
      paymentAuthReportData: null,
    });
  } finally {
    useResidualStore.setState({ paymentAuthReportLoading: false });
  }
}

export default useResidualStore;
